import { HiveClient, type ApiUser, type TokenStorage } from "@hive/sdk";
import { i18next, intlLocale } from "../i18n";
import { resolveConfig } from "../config";

// The auth token used to live here, in localStorage. It now lives in an HttpOnly cookie that
// the BFF sets on sign-in and reads back on every /v1 request (server/middlewares/session.ts),
// so this bundle can neither read it nor be robbed of it. What is left on this side is: a
// storage stub the SDK still requires, one-time cleanup of the old key, and the small set of
// same-origin session routes that replaced the token-issuing SDK calls.

/** The pre-cookie localStorage slot. Kept only so old browsers can be cleaned up. */
const LEGACY_TOKEN_KEY = "muhaha:token";

/**
 * The SDK needs a TokenStorage; there is nothing for it to store. Returning null makes it emit
 * token-less requests, and the /v1 proxy stamps Authorization from the session cookie on the
 * way out — so the entire non-auth API surface keeps working untouched.
 */
const noTokenStorage: TokenStorage = {
	getToken: () => null,
	setToken: () => {},
};

/**
 * Evicts the pre-migration token. Without this, every existing browser keeps a valid 7-day JWT
 * sitting in localStorage — exactly the exposure this change exists to remove — until it
 * happens to expire.
 */
function clearLegacyToken(): void {
	try {
		window.localStorage.removeItem(LEGACY_TOKEN_KEY);
	} catch {
		// Storage disabled (private mode, blocked cookies): nothing was ever stored either.
	}
}

if (typeof window !== "undefined") clearLegacyToken();

const config = resolveConfig();

/**
 * Default headers for every SDK request. Deliberately mutable: the SDK spreads
 * `opts.headers` at request time (not at construction), so updating this object
 * in place is enough to change the language of subsequent calls — no new client
 * needed. The backend treats `Accept-Language` as the fallback when a request
 * carries no explicit `?locale=`.
 */
export const requestHeaders: Record<string, string> = {
	"Accept-Language": intlLocale(),
};

export const hive = new HiveClient({
	baseUrl: config.baseUrl,
	appKey: config.appKey,
	storage: noTokenStorage,
	headers: requestHeaders,
	clientPlatform: "web",
	clientVersion: "muhaha-web/0.1.0",
	timeoutMs: 65_000,
	// Leaving/closing the page is when play time is flushed. Keep these tiny reports alive;
	// use the SDK's normal cookie transport, metadata and error handling for both writes.
	fetch: (input, init) => fetch(input, init?.method === "POST" && /\/playables\/[^/]+\/(engage|playtime)$/.test(String(input))
		? { ...init, keepalive: true } : init),
});

export function getClientConfig() {
	return config;
}

/** Persist the signed-in user's preferred UI locale to their profile. */
export function updateUserLocale(locale: string): Promise<ApiUser> {
	return hive.users.updateProfile({ locale });
}

interface ApiSuccessEnvelope<T> {
	ok: true;
	data: T;
}

interface ApiErrorEnvelope {
	ok: false;
	error?: { code?: string; message?: string };
}

/**
 * Calls one of the BFF's own session routes (server/routes/auth-session.ts). Same-origin, so
 * the session cookie rides along by default and the browser stamps the Origin header the
 * server's CSRF guard checks. `X-Client-Platform` is there for parity with the SDK, so Hive
 * sees the same client metadata whichever path a call took.
 */
export async function requestSession<T>(
	path: string,
	init: { method?: string; body?: unknown } = {},
): Promise<T> {
	const response = await fetch(path, {
		method: init.method ?? "POST",
		headers: {
			Accept: "application/json",
			"X-Client-Platform": "web",
			...(init.body === undefined ? {} : { "Content-Type": "application/json" }),
		},
		...(init.body === undefined ? {} : { body: JSON.stringify(init.body) }),
	});

	const json = (await response.json().catch(() => null)) as
		| ApiSuccessEnvelope<T>
		| ApiErrorEnvelope
		| null;
	if (!response.ok || !json || json.ok === false) {
		const failure = json && json.ok === false ? json.error : undefined;
		// `code` rides along because analytics reads it (analyticsErrorCode). The SDK used to
		// throw HiveApiError with a real code on these paths; a bare Error would report every
		// sign-in failure — rate limit, wrong code, blocked account — as the string "Error".
		throw Object.assign(new Error(failure?.message || i18next.t("auth.signInFailed")), {
			code: failure?.code,
			status: response.status,
		});
	}

	return json.data;
}

// Switching the UI language re-points `requestHeaders` in place; the already
// constructed `hive` client picks it up on its next request.
i18next.on("languageChanged", () => {
	requestHeaders["Accept-Language"] = intlLocale();
});
